import React from 'react';
import { ExternalLink, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const VOID_ELEMENTS = new Set(['br', 'hr', 'img']);
const ALLOWED_TAGS = new Set(['br', 'p', 'strong', 'b', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'li', 'a']);

/**
 * URL detection regex
 * Excludes trailing brackets and common punctuation.
 */
const URL_REGEX = /(?:https?:\/\/|www\.)[^\s<>"'\])\[(]+/gi;

/**
 * Parse href attribute from tag
 */
function parseHref(attrs: string): string | undefined {
  const match = attrs.match(/href=["']([^"']*)["']/i);
  return match?.[1];
}

/**
 * Parse style attribute (only padding-left supported)
 */
function parseStyle(attrs: string): React.CSSProperties | undefined {
  const match = attrs.match(/style=["']([^"']*)["']/i);
  if (!match) return undefined;

  const style: React.CSSProperties = {};
  match[1].split(';').forEach((rule) => {
    const [prop, val] = rule.split(':').map((s) => s.trim());
    if (prop === 'padding-left') style.paddingLeft = val;
  });

  return Object.keys(style).length ? style : undefined;
}

/**
 * Normalize URL (add https:// if starts with www.)
 */
function normalizeUrl(url: string): string {
  return url.startsWith('www.') ? `https://${url}` : url;
}

/**
 * Component for link buttons (open + copy)
 */
function LinkButtons({ url, keyPrefix }: { url: string; keyPrefix: number }) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(normalizeUrl(url));
      setCopied(true);
      toast.success('Скопировано');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Не удалось скопировать');
    }
  };

  const handleOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(normalizeUrl(url), '_blank', 'noopener,noreferrer');
  };

  return (
    <span key={`btns-${keyPrefix}`} className="inline-flex items-center gap-0.5 ml-1 align-middle">
      <button
        type="button"
        onClick={handleOpen}
        className="inline-flex items-center justify-center w-5 h-5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
        title="Открыть в новом окне"
      >
        <ExternalLink className="w-3 h-3" />
      </button>
      <button
        type="button"
        onClick={handleCopy}
        className="inline-flex items-center justify-center w-5 h-5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
        title="Копировать"
      >
        {copied ? (
          <Check className="w-3 h-3 text-green-600" />
        ) : (
          <Copy className="w-3 h-3" />
        )}
      </button>
    </span>
  );
}

/**
 * Process text to detect URLs and create elements with link buttons
 */
function processTextWithUrls(text: string, startKey: number): { elements: React.ReactNode[]; endKey: number } {
  const elements: React.ReactNode[] = [];
  let key = startKey;
  let lastIndex = 0;

  // Reset regex state
  URL_REGEX.lastIndex = 0;

  let match;
  while ((match = URL_REGEX.exec(text)) !== null) {
    const url = match[0];
    const startIndex = match.index;

    // Add text before the URL
    if (startIndex > lastIndex) {
      elements.push(text.slice(lastIndex, startIndex));
    }

    // Add the link with buttons
    elements.push(
      <React.Fragment key={key}>
        <a
          href={normalizeUrl(url)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline break-all"
          onClick={(e) => e.stopPropagation()}
        >
          {url}
        </a>
        <LinkButtons url={url} keyPrefix={key} />
      </React.Fragment>
    );
    key++;

    lastIndex = startIndex + url.length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    elements.push(text.slice(lastIndex));
  }

  // If no URLs found, return original text
  if (elements.length === 0) {
    elements.push(text);
  }

  return { elements, endKey: key };
}

/**
 * Parse simple HTML into React elements.
 * Only allows whitelisted tags: br, p, strong, h1-h6, ul, li, a
 * Attributes: href on <a>, style on <ul>
 * Also detects URLs in text and adds link buttons.
 */
export function parseHtmlToReact(html: string): React.ReactNode {
  if (!html) return null;

  const tokens: React.ReactNode[] = [];
  let key = 0;

  // Regex: text or tag
  const regex = /([^<]+)|<(\/?)(\w+)([^>]*)>/g;
  let match;

  const stack: { tag: string; children: React.ReactNode[]; props: Record<string, unknown> }[] = [];
  let current: React.ReactNode[] = tokens;

  while ((match = regex.exec(html)) !== null) {
    const [, text, isClose, tagName, attrs] = match;

    if (text) {
      // Process text for URLs and add elements
      const { elements, endKey } = processTextWithUrls(text, key);
      current.push(...elements);
      key = endKey;
      continue;
    }

    const tag = tagName.toLowerCase();

    if (!ALLOWED_TAGS.has(tag)) continue; // ignore unknown tags

    if (isClose) {
      // Closing tag - find matching opening tag in stack and close everything up to it
      let tagIndex = -1;
      for (let i = stack.length - 1; i >= 0; i--) {
        if (stack[i].tag === tag) {
          tagIndex = i;
          break;
        }
      }

      if (tagIndex >= 0) {
        // Close all tags from tagIndex to end of stack
        while (stack.length > tagIndex) {
          const item = stack.pop()!;
          const element = React.createElement(
            item.tag,
            { key: key++, ...item.props },
            item.children.length > 0 ? item.children : undefined
          );
          current = stack.length > 0 ? stack[stack.length - 1].children : tokens;
          current.push(element);
        }
      }
    } else if (VOID_ELEMENTS.has(tag)) {
      // Self-closing (br, hr)
      current.push(React.createElement(tag, { key: key++ }));
    } else {
      // Opening tag

      // Auto-close for <li>: close any ancestor <li> (and nested tags) before opening new one
      // This handles malformed HTML like: <li>...<ul><li>...</li><li>NEW without closing </ul></li>
      if (tag === 'li') {
        // Find if there's an ancestor <li> we need to close
        let liIndex = -1;
        for (let i = stack.length - 1; i >= 0; i--) {
          if (stack[i].tag === 'li') {
            liIndex = i;
            break;
          }
          // Stop at ul - don't look past the containing list
          if (stack[i].tag === 'ul') {
            break;
          }
        }

        // If we found a sibling <li> to close (in the same ul)
        if (liIndex >= 0 && (liIndex === stack.length - 1 || stack[liIndex + 1]?.tag === 'ul')) {
          // Close everything from that li onwards
          while (stack.length > liIndex) {
            const item = stack.pop()!;
            const element = React.createElement(
              item.tag,
              { key: key++, ...item.props },
              item.children.length > 0 ? item.children : undefined
            );
            current = stack.length > 0 ? stack[stack.length - 1].children : tokens;
            current.push(element);
          }
        }
      }

      // Auto-close same tag for other auto-close tags (like <p>)
      if (tag === 'p' && stack.length > 0 && stack[stack.length - 1].tag === 'p') {
        const item = stack.pop()!;
        const element = React.createElement(
          item.tag,
          { key: key++, ...item.props },
          item.children.length > 0 ? item.children : undefined
        );
        current = stack.length > 0 ? stack[stack.length - 1].children : tokens;
        current.push(element);
      }

      const props: Record<string, unknown> = {};

      if (tag === 'a') {
        const href = parseHref(attrs);
        if (href) props.href = href;
        props.target = '_blank';
        props.rel = 'noopener noreferrer';
        props.className = 'text-blue-600 hover:underline break-all';
      }

      if (tag === 'ul') {
        const style = parseStyle(attrs);
        if (style) props.style = style;
      }

      stack.push({ tag, children: [], props });
      current = stack[stack.length - 1].children;
    }
  }

  // Handle any unclosed tags by flushing remaining content
  while (stack.length > 0) {
    const item = stack.pop()!;
    const element = React.createElement(
      item.tag,
      { key: key++, ...item.props },
      item.children.length > 0 ? item.children : undefined
    );
    current = stack.length > 0 ? stack[stack.length - 1].children : tokens;
    current.push(element);
  }

  return tokens.length === 1 ? tokens[0] : tokens;
}
